import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import { AnimatePresence } from 'motion/react'
import {
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import {
  type ApplicantOrderKanbanEntry,
  type OrderListFilters,
  useGlobalKanbanColumn
} from '~/api/queries/order'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { KanbanColumn } from '~/components/Global/Blocks/Kanban/KanbanColumn'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { ApplicantCard } from '~/components/Scopes/Orders/OrderKanban/ApplicantCard'
import { ApplicantCardShimmer } from '~/components/Scopes/Orders/OrderKanban/ApplicantCardShimmer'
import {
  columnApplicants,
  columnApplicantsOver,
  columnApplicantsPlaceholder,
  columnOrderStatus
} from '~/components/Scopes/Orders/OrderKanban/OrderKanban.css'
import type { TaxonomyWithColor } from '~/domains/taxonomies/types'
import { useInfiniteList } from '~/hooks/useInfiniteList'
import { ApplicantModalContext } from '~/utils/contextes'

type GlobalKanbanColumnProps = {
  status: TaxonomyWithColor
  orderFilters: OrderListFilters
  canDrop?: boolean
}

export const GlobalKanbanColumn = ({
  status,
  orderFilters,
  canDrop = true
}: GlobalKanbanColumnProps): ReactElement => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [isOver, setIsOver] = useState(false)
  const { setApplicantCurrentId, setApplicantCollection } = useContext(
    ApplicantModalContext
  )

  const {
    entries,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useGlobalKanbanColumn({ statusId: status.id, orderFilters })

  const endOfListReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const { lastItemRef } = useInfiniteList({
    onEndOfListReached: endOfListReached,
    enabled: !isLoading && !isError && Boolean(hasNextPage)
  })

  useEffect(() => {
    const element = ref.current
    if (!element) return
    return dropTargetForElements({
      element,
      canDrop: () => canDrop,
      getData: () => ({ columnId: status.id }),
      onDragEnter: () => {
        if (canDrop) setIsOver(true)
      },
      onDragLeave: () => setIsOver(false),
      onDrop: () => setIsOver(false)
    })
  }, [canDrop, status.id])

  const openApplicantDetail = (entry: ApplicantOrderKanbanEntry) => {
    const collection = (entries ?? []).map((e) => ({
      id: e.applicant.id,
      firstname: e.applicant.firstname,
      lastname: e.applicant.lastname,
      order: {
        id: e.order.id,
        status: e.status.id,
        statusUpdatedAt: e.modified,
        statusNote: e.note
      }
    }))
    setApplicantCollection(collection)
    setApplicantCurrentId(entry.applicant.id)
  }

  return (
    <KanbanColumn
      isSpaced
      className={columnOrderStatus}
      title={<Badge backgroundColor={status.color} title={status.name} />}
    >
      <div
        key="applicants"
        ref={ref}
        className={clsx(columnApplicants, isOver ? columnApplicantsOver : null)}
      >
        <AnimatePresence>
          {(entries ?? []).map((entry) => (
            <div key={entry.id}>
              <ApplicantCard
                applicant={{
                  ...entry.applicant,
                  updatedAt: entry.modified,
                  note: entry.note ?? undefined
                }}
                orderBanner={entry.order}
                contextOrderId={entry.order.id}
                dragOrderId={entry.order.id}
                willChangeStatus
                showContracts={false}
                showScore={false}
                showNewApplicantBanner={false}
                openApplicantDetail={() => openApplicantDetail(entry)}
              />
            </div>
          ))}
        </AnimatePresence>
        {!isLoading && (entries ?? []).length === 0 && canDrop && (
          <div className={columnApplicantsPlaceholder}>
            Glissez un candidat ici pour le changer de statut
          </div>
        )}
        {isError && (
          <NoResult
            icon={faCircleExclamation}
            message="Une erreur est survenue lors du chargement."
            isError
            isSmall
          />
        )}
        {(isLoading || isFetchingNextPage) &&
          [...Array(isLoading ? 5 : 2)].map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: shimmer
            <ApplicantCardShimmer key={i} />
          ))}
        <div ref={lastItemRef} />
      </div>
    </KanbanColumn>
  )
}
